import {
  billingCellsForDesign,
  billableUnitsForDesign,
  type StudyDesign,
} from '../study-design';
import { isValidExposureBoundary } from './index';
import { lanesRepeatOver } from './exposure-coherence';

type RecordLike = Record<string, unknown>;

const list = (value: unknown): Array<RecordLike> =>
  Array.isArray(value) ? value.filter((v) => v && typeof v === 'object') : [];

const asRecord = (value: unknown): RecordLike =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as RecordLike)
    : {};

function positiveInt(value: number | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Rebuild draft billing facts from a compiled survey when only SurveyJS is available. */
function normalizeQuestionnaireBillingSource(input: RecordLike): RecordLike {
  if (list(input.researchQuestions).length > 0) {
    return input;
  }
  const config = asRecord(input._canonicalConfig);
  if (!Object.keys(config).length) {
    return input;
  }
  const questionMap = asRecord(config.questionMap);
  const researchQuestions: Array<RecordLike> = [];
  for (const page of list(input.pages)) {
    for (const element of list(asRecord(page).elements)) {
      const name = String(asRecord(element).name ?? '').trim();
      if (!name) {
        continue;
      }
      const mapped = asRecord(questionMap[name]);
      const stableKey = String(mapped.stableKey ?? mapped.questionId ?? '').trim();
      if (!stableKey) {
        continue;
      }
      researchQuestions.push({
        stableKey,
        ...(mapped.assetBinding !== undefined
          ? { assetBinding: mapped.assetBinding }
          : {}),
      });
    }
  }
  if (!researchQuestions.length) {
    for (const mapped of Object.values(questionMap)) {
      const entry = asRecord(mapped);
      const stableKey = String(entry.stableKey ?? entry.questionId ?? '').trim();
      if (!stableKey) {
        continue;
      }
      researchQuestions.push({
        stableKey,
        ...(entry.assetBinding !== undefined
          ? { assetBinding: entry.assetBinding }
          : {}),
      });
    }
  }
  return {
    ...input,
    researchQuestions,
    exposureBoundary: config.exposureBoundary ?? input.exposureBoundary,
    testedAssets: input.testedAssets,
  };
}

/**
 * Billing cells for a draft about to launch: twin monadic bills one cell per
 * cut only when lane questions repeat over each version (`lanesRepeatOver`).
 * Without lanes the engine runs a single comparative pass, so billing matches
 * comparative (one cell).
 */
export function billingCellsForQuestionnaireDraft(
  draft: RecordLike,
  design: StudyDesign,
  cutCount: number,
): number {
  if (design !== 'twin_monadic') {
    return billingCellsForDesign(design, cutCount);
  }
  const source = normalizeQuestionnaireBillingSource(draft);
  const questions = list(source.researchQuestions);
  const boundary = source.exposureBoundary;
  if (!questions.length || !isValidExposureBoundary(boundary)) {
    return billingCellsForDesign(design, cutCount);
  }
  const repeats = lanesRepeatOver(
    questions,
    boundary,
    source,
    design,
  );
  return repeats ? billingCellsForDesign(design, cutCount) : 1;
}

/** Billable units for a draft: respondents × billing cells from the draft shape. */
export function billableUnitsForQuestionnaireDraft(
  draft: RecordLike,
  design: StudyDesign,
  numRespondents: number,
  cutCount: number,
): number {
  return (
    positiveInt(numRespondents) *
    billingCellsForQuestionnaireDraft(draft, design, cutCount)
  );
}
